import React, { useContext, useEffect, useState } from 'react'
import { PermissionsEnum } from '@/api/Identity/Types/PermissionsEnum'
import {
  NPaymentProviderDto,
  NPaymentProviderMethodDefault,
  PaymentProviderOverviewKey,
} from '@/api/NPayments/Types/NPaymentProviderDto'
import {
  CurrencyMultiSelectFilterCell,
  WalletTypeMultiSelectFilterCell,
} from '@/components/Filter/common.filter'
import { multiSelectFilterCell } from '@/components/Filter/FilterCell'
import {
  FormBaseButtonActionWrapper,
  FormBaseNavigationHeader,
} from '@/components/Form/Form.styles'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { normalizeFilter } from '@/features/DataGrid'
import { getWalletCurrenciesAction, getWalletTypesAction } from '@/store/wallet/wallet-actions'
import { WalletsApiClient } from '@/utils/api/walletsApiClient'
import { AuthSessionContext } from '@/utils/AuthContext'
import { isEmpty } from '@/utils/is-empty'
import { getPaginationInitialState } from '@/utils/paginationInitialState'
import { hasPermission } from '@/utils/permissionsValidator'
import { CompositeFilterDescriptor, filterBy } from '@progress/kendo-data-query'
import { Page } from '@progress/kendo-react-dropdowns'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
} from '@progress/kendo-react-grid'
import { Modal } from 'components/CustomModal'
import MoreButton from 'components/MoreButton/MoreButton'
import { Form, Formik, FormikProps } from 'formik'
import { ChevronDown } from 'lucide-react'
import { useAppDispatch, useAppSelector } from 'store'
import { useApiClient } from 'utils/api/ApiClient'
import { v4 as uuid } from 'uuid'

import AddButton from '../../../components/AddButton/AddButton'
import ClientAreaAuditLogModal from '../../../components/ClientAreaAuditLogModal/ClientAreaAuditLogModal'
import PaymentProviderMethodDefaultForm from './PaymentProviderMethodDefaultForm'

export const NPaymentPSPDirectionTypes = [
  {
    id: 0,
    name: 'Deposit',
  },
  {
    id: 1,
    name: 'Withdrawal',
  },
]

const ConfigurationTypeMultiSelectFilterCell = multiSelectFilterCell({
  items: NPaymentPSPDirectionTypes.map((x) => ({ id: x.id, value: x.name, label: x.name })),
})

interface SubscriptionsDetailsMainProps {
  isEdit: boolean
  data: NPaymentProviderDto['methodDefaults']
  methodAddEditModalOpen(data?: NPaymentProviderMethodDefault): void
  onMethodDeleteModalOpen(data: NPaymentProviderMethodDefault): void
  onEnabledChange?(id: string, enabled: boolean): void
}

const PaymentProviderOptionsAndLimitTable: React.FC<SubscriptionsDetailsMainProps> = (props) => {
  const {
    data = [],
    methodAddEditModalOpen,
    onMethodDeleteModalOpen,
    isEdit,
    onEnabledChange,
  } = props
  const [paginationState, setPaginationState] = useState<Page>(getPaginationInitialState())
  const [filter, setFilter] = useState<CompositeFilterDescriptor>({
    logic: 'and',
    filters: [],
  })
  const { skip, take } = paginationState

  const handlePageChange = (event: GridPageChangeEvent) => {
    if (data) {
      setPaginationState({ skip: event.page.skip, take: event.page.take })
    }
  }

  const handleFilterChange = (event: GridFilterChangeEvent) => {
    setFilter(normalizeFilter(event.filter))
    setPaginationState((prev) => ({ ...prev, skip: 0 }))
  }

  const filteredData = filterBy(data, filter)

  return (
    <div>
      <Grid
        resizable
        data={filteredData.slice(skip, skip + take)}
        pageable
        style={{ zIndex: 0 }}
        total={filteredData.length}
        onPageChange={handlePageChange}
        skip={skip}
        take={take}
        filterable={true}
        filter={filter}
        onFilterChange={handleFilterChange}
      >
        <Column
          field={PaymentProviderOverviewKey.walletType}
          title='Wallet Type'
          filterCell={WalletTypeMultiSelectFilterCell}
          cell={(props: GridCellProps) => {
            if (!props.dataItem?.walletType?.name) {
              return <td />
            }
            return <td>{props.dataItem.walletType.name}</td>
          }}
        />
        <Column
          field={PaymentProviderOverviewKey.paymentCurrency}
          title='Currency'
          filterCell={CurrencyMultiSelectFilterCell}
        />
        <Column
          field={PaymentProviderOverviewKey.configurationType}
          title='Type'
          filterCell={ConfigurationTypeMultiSelectFilterCell}
        />
        <Column
          className='table-columns__number'
          field={PaymentProviderOverviewKey.minimumAmount}
          title='Minimum'
          filter='numeric'
        />
        <Column
          className='table-columns__number'
          field={PaymentProviderOverviewKey.maximumAmount}
          title='Maximum'
          filter='numeric'
        />
        <Column
          field={PaymentProviderOverviewKey.enabled}
          title='Enabled'
          filterCell={() => null}
          cell={(props: GridCellProps) => (
            <td>
              <input
                type='checkbox'
                disabled={!isEdit}
                checked={props.dataItem.enabled}
                onChange={(e) => {
                  if (onEnabledChange) {
                    onEnabledChange(props.dataItem.id, e.target.checked)
                  }
                }}
              />
            </td>
          )}
        />
        <Column
          field={PaymentProviderOverviewKey.maintenance}
          title='Maintenance'
          filterCell={() => null}
          cell={(props: GridCellProps) => (
            <td align='center' style={{ textAlign: 'center' }}>
              <input type='checkbox' checked={props.dataItem.maintenance} disabled />
            </td>
          )}
        />
        <Column
          width={40}
          title=''
          filterCell={() => null}
          cell={(gridCellProps: GridCellProps) => (
            <td>
              <MoreButton
                items={[
                  {
                    name: 'Edit',
                    disabled: !isEdit,
                    'data-test': 'edit',
                    onClick: () => methodAddEditModalOpen(gridCellProps.dataItem),
                  },
                  {
                    name: 'Delete',
                    'data-test': 'delete',
                    disabled: !isEdit,
                    onClick: () => onMethodDeleteModalOpen(gridCellProps.dataItem),
                  },
                ]}
                data-test='more-button'
              />
            </td>
          )}
        />
      </Grid>
    </div>
  )
}

interface OuterProps {
  data: NPaymentProviderDto
  handleSubmit(config: NPaymentProviderDto): void
  isEdit: boolean
  setEdit: React.Dispatch<React.SetStateAction<boolean>>
}

interface FormValues {
  methodDefaults: NPaymentProviderDto['methodDefaults']
}

export const PaymentProviderOptionsAndLimitsDetailsPage: React.FC<OuterProps> = (props) => {
  const { data, handleSubmit, isEdit, setEdit } = props
  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []
  const [isPSPMethodDefaultAddEditModalOpen, setPSPMethodDefaultAddEditModalOpen] = useState<{
    data?: NPaymentProviderMethodDefault
    visible: boolean
  }>({
    visible: false,
    data: undefined,
  })
  const [auditLogVisible, setAuditLogVisible] = useState<boolean>(false)

  const walletTypes = useAppSelector((state) => state.wallet.walletType)
  const dispatch = useAppDispatch()
  const walletsApiClient = useApiClient(WalletsApiClient)
  const walletCurrencies = useAppSelector((state) => state.wallet.currencies)

  useEffect(() => {
    if (isEmpty(walletTypes)) {
      dispatch(getWalletTypesAction(walletsApiClient))
    }
    if (isEmpty(walletCurrencies)) {
      dispatch(getWalletCurrenciesAction(walletsApiClient))
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const initialValues: FormValues = {
    methodDefaults: data.methodDefaults,
  }

  return (
    <React.Fragment>
      {auditLogVisible && (
        <ClientAreaAuditLogModal
          isVisible={auditLogVisible}
          onClose={() => setAuditLogVisible(false)}
          paymentProviderId={data.id}
        />
      )}
      <Formik
        initialValues={initialValues}
        onSubmit={async (values, actions) => {
          handleSubmit({ ...data, methodDefaults: values.methodDefaults })
          actions.setSubmitting(false)
        }}
      >
        {(formikProps: FormikProps<FormValues>) => {
          const handleMethodDefaultSubmit = (values: NPaymentProviderMethodDefault) => {
            const currentMethodDefaults = formikProps.values.methodDefaults
            let updatedMethodDefaults: NPaymentProviderMethodDefault[]

            if (values.id) {
              updatedMethodDefaults = currentMethodDefaults.map((md) =>
                md.id === values.id ? values : md
              )
            } else {
              updatedMethodDefaults = [{ ...values, id: uuid() }, ...currentMethodDefaults]
            }

            formikProps.setFieldValue('methodDefaults', updatedMethodDefaults)
            setPSPMethodDefaultAddEditModalOpen({ data: undefined, visible: false })
          }

          return (
            <Form>
              <FormBaseNavigationHeader style={{ justifyContent: 'space-between' }}>
                <AddButton
                  disabled={
                    !isEdit || !hasPermission(permissions, PermissionsEnum.CanEditPaymentProviders)
                  }
                  text='Add new option'
                  onClick={() =>
                    setPSPMethodDefaultAddEditModalOpen({
                      data: undefined,
                      visible: true,
                    })
                  }
                  data-test='add-new-country-button'
                />
                <FormBaseButtonActionWrapper style={{ margin: '1rem' }}>
                  <ButtonGroup>
                    {!isEdit ? (
                      <Button
                        type='button'
                        disabled={
                          !hasPermission(permissions, PermissionsEnum.CanEditPaymentProviders)
                        }
                        onClick={() => setEdit(true)}
                      >
                        Edit
                      </Button>
                    ) : (
                      <>
                        <Button
                          type='button'
                          variant='outline'
                          onClick={() => {
                            setEdit(false)
                            formikProps.resetForm()
                          }}
                        >
                          Cancel
                        </Button>
                        <Button type='submit'>Save</Button>
                      </>
                    )}

                    <DropdownMenu>
                      <DropdownMenuTrigger
                        render={
                          <Button
                            type='button'
                            variant='outline'
                            size='icon'
                            aria-label='More options'
                          />
                        }
                      >
                        <ChevronDown />
                      </DropdownMenuTrigger>

                      <DropdownMenuContent align='end'>
                        <DropdownMenuItem
                          onSelect={() => setAuditLogVisible(true)}
                          data-test='audit-log-button'
                        >
                          Audit Log
                        </DropdownMenuItem>

                        <DropdownMenuSub>
                          <DropdownMenuSubTrigger data-test='status-menu-item'>
                            Status
                          </DropdownMenuSubTrigger>
                          <DropdownMenuSubContent>
                            <DropdownMenuItem
                              disabled={
                                !isEdit
                                  ? data.enabled ||
                                    !hasPermission(
                                      permissions,
                                      PermissionsEnum.CanEditPaymentProviders
                                    )
                                  : data.enabled
                              }
                              onSelect={() => {
                                handleSubmit({
                                  ...data,
                                  enabled: true,
                                })
                              }}
                              data-test='enable-status'
                            >
                              Enable
                            </DropdownMenuItem>

                            <DropdownMenuItem
                              disabled={
                                !isEdit
                                  ? !data.enabled ||
                                    !hasPermission(
                                      permissions,
                                      PermissionsEnum.CanEditPaymentProviders
                                    )
                                  : !data.enabled
                              }
                              onSelect={() => {
                                handleSubmit({
                                  ...data,
                                  enabled: false,
                                })
                              }}
                              data-test='disable-status'
                            >
                              Disable
                            </DropdownMenuItem>
                          </DropdownMenuSubContent>
                        </DropdownMenuSub>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </ButtonGroup>
                </FormBaseButtonActionWrapper>
              </FormBaseNavigationHeader>

              {isPSPMethodDefaultAddEditModalOpen.visible && (
                <Modal
                  width={600}
                  visible={isPSPMethodDefaultAddEditModalOpen.visible}
                  onClose={() =>
                    setPSPMethodDefaultAddEditModalOpen({
                      data: undefined,
                      visible: false,
                    })
                  }
                >
                  <PaymentProviderMethodDefaultForm
                    walletType={walletTypes}
                    directionTypes={NPaymentPSPDirectionTypes}
                    walletCurrencies={walletCurrencies}
                    v={
                      isPSPMethodDefaultAddEditModalOpen.data
                        ? {
                            id: isPSPMethodDefaultAddEditModalOpen.data.id,
                            walletTypeId: isPSPMethodDefaultAddEditModalOpen.data.walletType.id,
                            currencyId: isPSPMethodDefaultAddEditModalOpen.data.paymentCurrency.id,
                            typeId: isPSPMethodDefaultAddEditModalOpen.data.configurationType.id,
                            minimumAmount: isPSPMethodDefaultAddEditModalOpen.data.minimumAmount,
                            maximumAmount: isPSPMethodDefaultAddEditModalOpen.data.maximumAmount,
                            enabled: isPSPMethodDefaultAddEditModalOpen.data.enabled,
                            maintenance: isPSPMethodDefaultAddEditModalOpen.data.maintenance,
                          }
                        : {
                            id: '',
                            walletTypeId: undefined,
                            currencyId: '',
                            typeId: undefined,
                            minimumAmount: 0,
                            maximumAmount: 0,
                            enabled: true,
                            maintenance: false,
                          }
                    }
                    onSubmit={handleMethodDefaultSubmit}
                    onClose={() =>
                      setPSPMethodDefaultAddEditModalOpen({
                        data: undefined,
                        visible: false,
                      })
                    }
                  />
                </Modal>
              )}

              <PaymentProviderOptionsAndLimitTable
                isEdit={isEdit}
                data={formikProps.values.methodDefaults}
                onMethodDeleteModalOpen={(d) => {
                  const filtered = formikProps.values.methodDefaults.filter(
                    (item) => item.id !== d.id
                  )
                  formikProps.setFieldValue('methodDefaults', filtered)
                }}
                methodAddEditModalOpen={(d) =>
                  setPSPMethodDefaultAddEditModalOpen({ data: d, visible: true })
                }
                onEnabledChange={(id, enabled) => {
                  const updatedMethodDefaults = formikProps.values.methodDefaults.map((item) =>
                    item.id === id ? { ...item, enabled } : item
                  )
                  formikProps.setFieldValue('methodDefaults', updatedMethodDefaults)
                }}
              />
            </Form>
          )
        }}
      </Formik>
    </React.Fragment>
  )
}
